/**
 * 결제(Billing) API 파사드 — `/api/fo/ws/billing/**`(specs/013 토스 정기결제). 화면은 시그니처만 의존한다.
 * 구독 재개·쿠폰 검증은 백엔드 엔드포인트가 없어 "준비 중" ApiError 로 거절한다(spec 028 FR-028 — 범위 밖, 백엔드 신설 시 본문만 교체).
 */
import type {
  CancelResponse,
  CancelSubscriptionRequest,
  CheckoutConfirmRequest,
  CheckoutConfirmResponse,
  CheckoutPrepareResponse,
  CouponValidateResponse,
  MySubscriptionResponse,
  PaymentMethodPrepareResponse,
  PaymentMethodResponse,
  ReplacePaymentMethodRequest,
} from '@/features/subscription/types';
import { notify } from '@/lib/toast';

import { ApiError, http, withErrors } from './http';

const BASE_URL = '/fo/ws/billing';
/** 미구현 엔드포인트 상태 — 화면은 ApiError 메시지만 표시한다. */
const NOT_IMPLEMENTED = 501;

/** 결제창 진입·콜백 확인 — 화면(결제 패널·콜백 페이지)이 오류를 인라인으로 보여 주므로 토스트는 끈다. 401 은 로그인 이동 유지. */
const INLINE_ERRORS = withErrors({ silentWhen: (e) => e.status !== 401 });

/** 결제 준비 — 요금제 → 결제창 오픈 파라미터(orderId·customerKey·서버 확정 금액) 발급. 쿠폰은 서버 미지원이라 전달하지 않는다. */
export function prepareCheckout(
  planId: number,
  _couponCode?: string,
): Promise<CheckoutPrepareResponse> {
  return http
    .post<CheckoutPrepareResponse>(`${BASE_URL}/checkout/prepare`, { planId }, INLINE_ERRORS)
    .then((r) => r.data);
}

/** 결제창 콜백 확인 — 빌링키 발급 + 첫 청구 승인 → 구독 유료 전환. */
export function confirmCheckout(payload: CheckoutConfirmRequest): Promise<CheckoutConfirmResponse> {
  return http
    .post<CheckoutConfirmResponse>(`${BASE_URL}/checkout/confirm`, payload, INLINE_ERRORS)
    .then((r) => r.data);
}

/** 구독 해지 — 연간+refund=true 는 서버가 일할 환불까지 처리. */
export function cancelSubscription(payload: CancelSubscriptionRequest): Promise<CancelResponse> {
  return http
    .delete<CancelResponse>(`${BASE_URL}/subscription`, { data: payload })
    .then((r) => r.data);
}

/** 해지 구독 재개(자동갱신 ON 복귀) — 결제수단 보유 CANCELED 전용. */
export function resumeSubscription(): Promise<MySubscriptionResponse> {
  const error = new ApiError(
    NOT_IMPLEMENTED,
    '구독 재개는 준비 중입니다. 고객센터로 문의해 주세요.',
  );
  notify.error(error.message);
  return Promise.reject(error);
}

/** 결제수단 조회 — 미등록(Trial 등)은 `{ registered: false }`. */
export function getPaymentMethod(): Promise<PaymentMethodResponse> {
  return http.get<PaymentMethodResponse>(`${BASE_URL}/method`).then((r) => r.data);
}

/** 결제수단 교체 준비 — ACTIVE/PAST_DUE 전용, planId 불요(기존 customerKey 반환·청구 없음). */
export function prepareReplace(): Promise<PaymentMethodPrepareResponse> {
  return http
    .post<PaymentMethodPrepareResponse>(
      `${BASE_URL}/method/replace/prepare`,
      undefined,
      INLINE_ERRORS,
    )
    .then((r) => r.data);
}

/** 결제수단 교체 — 콜백 흐름 재사용, 첫 청구 없이 빌링키만 UPDATE. */
export function replacePaymentMethod(
  payload: ReplacePaymentMethodRequest,
): Promise<PaymentMethodResponse> {
  return http
    .post<PaymentMethodResponse>(`${BASE_URL}/method/replace/confirm`, payload, INLINE_ERRORS)
    .then((r) => r.data);
}

/** 쿠폰 검증 — 할인액은 서버 확정값. 무효는 404, 만료는 410. */
export function validateCoupon(_code: string, _planId: number): Promise<CouponValidateResponse> {
  return Promise.reject(new ApiError(NOT_IMPLEMENTED, '쿠폰 기능은 준비 중입니다.'));
}
